// Behavior tracker — module singleton.
// MUST stay a module singleton: AuthProvider is mounted twice (index.js and
// App.js) and StrictMode double-fires effects in dev, so nothing here may
// live in React state. Every public method is try/catch-wrapped so the
// tracker can never crash or block the app; losing an event is always
// preferable to breaking a page.
import { APP_CONFIG } from '../config/appConfig';
import { getFingerprint } from './fingerprint';

const BASE_URL = APP_CONFIG.API_BASE_URL;

const SID_KEY = 'ls_sid';
const SID_LAST_KEY = 'ls_sid_last';
const SESSION_IDLE_MS = 30 * 60 * 1000;

const FLUSH_INTERVAL_MS = 20 * 1000;
const HEARTBEAT_ONLY_FLUSH_MS = 60 * 1000;
const FLUSH_AT_QUEUE_SIZE = 10;
const QUEUE_HARD_CAP = 100;
const MAX_BATCH = 20;

const HEARTBEAT_MS = 30 * 1000;
const TOUCH_MIN_GAP_MS = 25 * 1000;

const MAX_JS_ERRORS = 10;
const MAX_API_ERRORS = 20;
const PAGE_VIEW_DEDUP_MS = 500;

// Tracks visible (foreground) time. take() returns accumulated visible ms
// since the last take() and restarts the counter.
const makeStopwatch = () => {
  let accum = 0;
  let visibleSince = document.visibilityState === 'visible' ? Date.now() : null;
  return {
    pause() {
      if (visibleSince !== null) {
        accum += Date.now() - visibleSince;
        visibleSince = null;
      }
    },
    resume() {
      if (visibleSince === null) visibleSince = Date.now();
    },
    take() {
      const live = visibleSince !== null ? Date.now() - visibleSince : 0;
      const total = accum + live;
      accum = 0;
      if (visibleSince !== null) visibleSince = Date.now();
      return Math.max(0, Math.round(total));
    },
  };
};

const state = {
  initialized: false,
  queue: [],
  lastFlushAt: 0,
  flushing: false,
  sessionId: null,
  fp: null,
  currentPath: null,
  lastPageViewPath: null,
  lastPageViewAt: 0,
  lastHeartbeatAt: 0,
  jsErrorCount: 0,
  apiErrorCount: 0,
  seenErrors: new Set(),
  pageWatch: null,
  hbWatch: null,
};

const newUuid = () => {
  try {
    return window.crypto.randomUUID();
  } catch {
    // very old browsers: RFC4122-ish fallback
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }
};

const readSearchParam = (name) => {
  try {
    return new URLSearchParams(window.location.search).get(name) || null;
  } catch {
    return null;
  }
};

const buildSessionStartMeta = () => {
  const meta = {};
  try {
    if (document.referrer) meta.referrer = document.referrer.slice(0, 200);
    meta.landingPage = window.location.pathname.slice(0, 100);
    const utmSource = readSearchParam('utm_source');
    const utmMedium = readSearchParam('utm_medium');
    const utmCampaign = readSearchParam('utm_campaign');
    const ref = readSearchParam('ref');
    if (utmSource) meta.utmSource = utmSource.slice(0, 80);
    if (utmMedium) meta.utmMedium = utmMedium.slice(0, 80);
    if (utmCampaign) meta.utmCampaign = utmCampaign.slice(0, 80);
    if (ref) meta.ref = ref.slice(0, 50);
    if (window.screen) {
      meta.screenW = window.screen.width;
      meta.screenH = window.screen.height;
    }
    if (navigator.language) meta.lang = navigator.language.slice(0, 12);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) meta.tz = tz.slice(0, 40);
  } catch {
    // partial meta is fine
  }
  return meta;
};

// Returns the current session id; rotates after 30 min of inactivity and
// enqueues a session_start for every new session.
const ensureSession = () => {
  const now = Date.now();
  let sid = state.sessionId;
  let last = 0;
  try {
    sid = sid || sessionStorage.getItem(SID_KEY);
    last = parseInt(sessionStorage.getItem(SID_LAST_KEY) || '0', 10) || 0;
  } catch {
    // sessionStorage blocked — fall back to in-memory session
  }

  if (!sid || (last && now - last > SESSION_IDLE_MS)) {
    sid = newUuid();
    state.sessionId = sid;
    try {
      sessionStorage.setItem(SID_KEY, sid);
    } catch {}
    state.queue.push({
      type: 'session_start',
      page: window.location.pathname,
      sessionId: sid,
      meta: buildSessionStartMeta(),
    });
  }

  state.sessionId = sid;
  try {
    sessionStorage.setItem(SID_LAST_KEY, String(now));
  } catch {}
  return sid;
};

const enqueue = (type, meta = {}, page = null) => {
  const sid = ensureSession();
  if (state.queue.length >= QUEUE_HARD_CAP) state.queue.shift();
  state.queue.push({
    type,
    page: page || state.currentPath || window.location.pathname,
    sessionId: sid,
    meta,
  });
  if (state.queue.length >= FLUSH_AT_QUEUE_SIZE) flush();
};

const flush = async (useKeepalive = false) => {
  if (state.flushing || state.queue.length === 0) return;
  state.flushing = true;
  const batch = state.queue.splice(0, MAX_BATCH);
  state.lastFlushAt = Date.now();
  try {
    // session_start must be first so the backend can mint the visitor cookie
    batch.sort((a, b) => (a.type === 'session_start' ? -1 : 0) - (b.type === 'session_start' ? -1 : 0));
    // attach the fingerprint if it resolved after the session_start was queued
    let hasSessionStart = false;
    for (const e of batch) {
      if (e.type === 'session_start') {
        hasSessionStart = true;
        // two independent fingerprint hashes — see utils/fingerprint.js.
        // Either can be missing (e.g. no WebGL) without affecting the other.
        if (state.fp) {
          if (!e.meta.primaryFp && state.fp.primary) e.meta.primaryFp = state.fp.primary.hash;
          if (!e.meta.secondaryFp && state.fp.secondary) e.meta.secondaryFp = state.fp.secondary.hash;
        }
      }
    }
    const payload = { events: batch };
    // raw fingerprint components ride along so the backend registry can show
    // admins what each hash was built from
    if (hasSessionStart && state.fp) {
      payload.fpInfo = {
        primary: (state.fp.primary && state.fp.primary.components) || {},
        secondary: (state.fp.secondary && state.fp.secondary.components) || {},
      };
    }
    const body = JSON.stringify(payload);
    await fetch(`${BASE_URL}/track/events`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      keepalive: useKeepalive && body.length < 60000,
      body,
    });
  } catch {
    // network failure: requeue once, then give up (never loop)
    const retriable = batch.filter((e) => !e._retried);
    retriable.forEach((e) => { e._retried = true; });
    state.queue = retriable.concat(state.queue).slice(0, QUEUE_HARD_CAP);
  } finally {
    state.flushing = false;
  }
};

const trackJsError = (message, source, line) => {
  try {
    if (state.jsErrorCount >= MAX_JS_ERRORS) return;
    const msg = String(message || 'Unknown error').slice(0, 200);
    const key = `js:${msg}`;
    if (state.seenErrors.has(key)) return;
    state.seenErrors.add(key);
    state.jsErrorCount++;
    enqueue('js_error', {
      message: msg,
      ...(source ? { source: String(source).slice(0, 120) } : {}),
      ...(Number.isFinite(line) ? { line } : {}),
    });
  } catch {}
};

const trackApiError = (url, status, method) => {
  try {
    if (state.apiErrorCount >= MAX_API_ERRORS) return;
    let endpoint = url;
    try {
      endpoint = new URL(url, window.location.origin).pathname;
    } catch {}
    endpoint = String(endpoint).slice(0, 100);
    const key = `api:${endpoint}:${status}`;
    if (state.seenErrors.has(key)) return;
    state.seenErrors.add(key);
    state.apiErrorCount++;
    enqueue('api_error', {
      endpoint,
      status: Number.isFinite(status) ? status : 0,
      ...(method ? { method: String(method).toUpperCase().slice(0, 10) } : {}),
    });
  } catch {}
};

const isTrackedApiUrl = (url) => {
  if (typeof url !== 'string') return false;
  const isApi =
    url.startsWith(APP_CONFIG.API_BASE_URL) ||
    (APP_CONFIG.BASE_BACKEND_URL && url.startsWith(APP_CONFIG.BASE_BACKEND_URL));
  return isApi && !url.includes('/track/');
};

// Observe (never modify) API responses. Requests/responses pass through
// untouched; bodies are never read; original errors are always re-seen by
// the caller unchanged.
const wrapFetch = () => {
  if (window.__lsFetchWrapped) return;
  window.__lsFetchWrapped = true;
  const origFetch = window.fetch;
  window.fetch = function (...args) {
    const promise = origFetch.apply(this, args);
    try {
      const input = args[0];
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      if (isTrackedApiUrl(url)) {
        const method =
          (args[1] && args[1].method) ||
          (input && typeof input === 'object' && input.method) ||
          'GET';
        promise.then(
          (res) => {
            if (res && res.status >= 400) trackApiError(url, res.status, method);
          },
          () => trackApiError(url, 0, method)
        );
      }
    } catch {}
    return promise;
  };
};

const heartbeat = () => {
  try {
    if (document.visibilityState !== 'visible') return;
    state.lastHeartbeatAt = Date.now();
    const activeMs = state.hbWatch ? Math.min(state.hbWatch.take(), 120000) : 0;
    enqueue('heartbeat', activeMs > 0 ? { activeMs } : {});
  } catch {}
};

const tick = () => {
  try {
    const now = Date.now();
    if (now - state.lastHeartbeatAt >= HEARTBEAT_MS) heartbeat();
    if (state.queue.length === 0) return;
    const hasRealEvents = state.queue.some((e) => e.type !== 'heartbeat');
    const sinceFlush = now - state.lastFlushAt;
    // real events flush at the normal cadence; heartbeat-only batches wait
    // longer so idle tabs cost one request a minute, not three
    if (hasRealEvents && sinceFlush >= FLUSH_INTERVAL_MS) flush();
    else if (!hasRealEvents && sinceFlush >= HEARTBEAT_ONLY_FLUSH_MS) flush();
  } catch {}
};

const tracker = {
  init() {
    try {
      if (state.initialized) return;
      state.initialized = true;

      state.pageWatch = makeStopwatch();
      state.hbWatch = makeStopwatch();
      state.currentPath = window.location.pathname;
      state.lastHeartbeatAt = Date.now();

      getFingerprint().then((fp) => { state.fp = fp; }).catch(() => {});

      wrapFetch();

      window.addEventListener('error', (e) => {
        trackJsError(e.message, e.filename, e.lineno);
      });
      window.addEventListener('unhandledrejection', (e) => {
        const reason = e && e.reason;
        trackJsError(
          (reason && (reason.message || String(reason))) || 'Unhandled rejection',
          null,
          null
        );
      });

      document.addEventListener('visibilitychange', () => {
        try {
          if (document.visibilityState === 'hidden') {
            if (state.pageWatch) state.pageWatch.pause();
            if (state.hbWatch) state.hbWatch.pause();
            flush(true);
          } else {
            if (state.pageWatch) state.pageWatch.resume();
            if (state.hbWatch) state.hbWatch.resume();
          }
        } catch {}
      });
      window.addEventListener('pagehide', () => {
        try {
          heartbeat();
          flush(true);
        } catch {}
      });

      setInterval(tick, 10 * 1000);
    } catch {}
  },

  // Route changes. Emits page_leave (with visible time) for the previous
  // page, then page_view for the new one. Deduped against StrictMode's
  // double-fired effects.
  trackPageView(path) {
    try {
      if (!state.initialized) tracker.init();
      const now = Date.now();
      if (path === state.lastPageViewPath && now - state.lastPageViewAt < PAGE_VIEW_DEDUP_MS) return;

      if (state.lastPageViewPath !== null && state.lastPageViewPath !== path && state.pageWatch) {
        const activeMs = Math.min(state.pageWatch.take(), 7200000);
        enqueue('page_leave', activeMs > 0 ? { activeMs } : {}, state.lastPageViewPath);
      } else if (state.pageWatch) {
        state.pageWatch.take(); // restart the counter without emitting
      }

      state.lastPageViewPath = path;
      state.lastPageViewAt = now;
      state.currentPath = path;
      enqueue('page_view', {}, path);
    } catch {}
  },

  // Explicit product events (CTA clicks, checkout steps, ...).
  track(type, meta = {}) {
    try {
      if (!state.initialized) tracker.init();
      enqueue(type, meta);
    } catch {}
  },

  // Current tracking session id (used by logout to close the session server-side)
  getSessionId() {
    try {
      return state.sessionId || sessionStorage.getItem(SID_KEY) || null;
    } catch {
      return state.sessionId;
    }
  },

  // Backward-compatible replacement for AuthContext.reportActivity:
  // components call it around important actions; it refreshes lastActivity
  // via a heartbeat, throttled so old call sites can't spam.
  touch() {
    try {
      if (!state.initialized) tracker.init();
      if (Date.now() - state.lastHeartbeatAt >= TOUCH_MIN_GAP_MS) heartbeat();
    } catch {}
  },

  // Login/logout: flush what belongs to the previous identity, then rotate
  // the session. The next event starts a fresh session whose session_start
  // runs under the NEW identity — this is what attaches the fingerprint to
  // every account used in this browser, not just the first one.
  onAuthChange() {
    try {
      if (!state.initialized) return;
      flush();
      state.sessionId = null;
      try {
        sessionStorage.removeItem(SID_KEY);
        sessionStorage.removeItem(SID_LAST_KEY);
      } catch {}
    } catch {}
  },
};

export default tracker;
